import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Tooltip, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axiosInstance';
import {
  CloseOutlined,
  SendOutlined,
  ReloadOutlined,
  CopyOutlined,
  UserOutlined,
  LinkOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
  CompassOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
  CheckCircleFilled,
  BulbFilled,
  OrderedListOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import VinnyIcon from './VinnyIcon';
import SupportTicketForm from './SupportTicketForm';
import './vinnyChat.css';

const { TextArea } = Input;

const DEFAULT_ERROR_MESSAGE =
  "Sorry, I'm having trouble connecting right now. Please try again.";

const NO_ANSWER_MESSAGE = "I'm here, but I couldn't find an answer this time.";

// Recursively extracts plain text from ReactMarkdown children so renderers
// can detect semantic labels (Result:/Tip:/Navigation:) and pick icons.
const getNodeText = (node) => {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (node.props && node.props.children) return getNodeText(node.props.children);
  return '';
};

// Older cached answers carried emoji markers; the UI now renders proper
// icons itself, so strip the legacy markers from bot messages.
const stripLegacyEmojiMarkers = (text) =>
  typeof text === 'string' ? text.replace(/[📍📋✅💡🎥]\s?/gu, '') : text;

const FAILURE_PHRASES = [
  NO_ANSWER_MESSAGE,
  "doesn't cover that specific detail",
  "didn't quite get that",
  "checked the guide",
  "I didn't understand",
  "connecting you with our L2 Support Team",
  // Additional phrases to detect undocumented technical questions
  "don't have documentation",
  "don't have the specific documentation",
  "don't have the documentation",
  "not available in my current knowledge",
  "not in my current knowledge base",
  "I don't have information",
  "I don't have specific information",
  "I am connecting you with",
  "Click the button below"
];

const resolveResponseText = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if (typeof payload.answer === 'string') {
    return payload.answer;
  }
  if (typeof payload.text === 'string') {
    return payload.text;
  }
  if (typeof payload.message === 'string') {
    return payload.message;
  }
  if (typeof payload.response === 'string') {
    return payload.response;
  }
  if (typeof payload.reply === 'string') {
    return payload.reply;
  }
  return null;
};

// LocalStorage key prefixes for user-specific persistence
const VINNY_THREAD_ID_PREFIX = 'vinny_chat_thread_id_';
const VINNY_MESSAGES_PREFIX = 'vinny_chat_messages_';

const VinnyChat = ({
  apiEndpoint = '/vinny/chat/query',
  // clearApiEndpoint = '/vinny/chat/clear', 
  title = 'Vinny',
  welcomeMessage = "Hi there! I'm Vinny. How can I help you today?",
  placeholder = 'Ask me anything...',
  initialOpen = false,
  userId = null, // Required for user-specific chat isolation
  userEmail = null, // Logged-in user's email; pre-fills the support ticket form
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messageContainerRef = useRef(null);
  const textAreaRef = useRef(null);
  const { token } = theme.useToken();

  // Custom link renderer for ReactMarkdown - handles navigation links
  const linkRenderer = useCallback(({ href, children }) => {
    // Separate links into three categories: 
    // 1. True app-internal links (relative paths)
    // 2. Company resource links (Sharepoint/Wiki - styled as buttons but open externally)
    // 3. General external links

    const isAppInternal = href && href.startsWith('/');
    const isResourceLink = href && (
      href.includes('wikiworks.in') ||
      href.includes('wikiworkstech') ||
      href.includes('sharepoint.com')
    );

    // Common styling for button-like links. No fixed height/line-height here:
    // long labels ("Go to Employee Management") wrap to two lines inside the
    // ~340px chat bubble, and a fixed 28px height clipped the second line so
    // it overlapped the arrow icon. minHeight + normal white-space + a
    // dedicated text wrapper (flex: 1) let it grow to fit wrapped text while
    // keeping the icon aligned and un-squashed.
    const buttonStyle = {
      padding: '6px 12px',
      // Explicit 'auto' overrides antd's .ant-btn-sm class (which sets a
      // fixed height via CSS) now that we no longer pin our own fixed height.
      height: 'auto',
      minHeight: '28px',
      fontSize: '13px',
      lineHeight: '1.4',
      color: '#fff',
      backgroundColor: token.colorPrimary,
      border: 'none',
      borderRadius: '14px',
      margin: '4px 0',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      textDecoration: 'none',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontWeight: 500,
      whiteSpace: 'normal',
      maxWidth: '100%',
      boxSizing: 'border-box',
    };
    const buttonTextStyle = { flex: 1, minWidth: 0 };
    const buttonIconStyle = { flexShrink: 0 };

    // Video/tutorial links get a play icon; other resources keep the link icon
    const isVideoLink = /video|tutorial|watch/i.test(getNodeText(children));

    if (isAppInternal) {
      const cleanPath = href.replace(/#$/, '');
      return (
        <a
          href={cleanPath}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
              return;
            }
            e.preventDefault();
            navigate(cleanPath);
          }}
          className="ant-btn ant-btn-sm vinny-nav-link"
          style={buttonStyle}
        >
          <span style={buttonTextStyle}>{children}</span>
          <ArrowRightOutlined style={buttonIconStyle} />
        </a>
      );
    }

    if (isResourceLink) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="ant-btn ant-btn-sm vinny-nav-link"
          style={buttonStyle}
        >
          {isVideoLink ? <PlayCircleOutlined style={buttonIconStyle} /> : <LinkOutlined style={buttonIconStyle} />}
          <span style={buttonTextStyle}>{children}</span>
        </a>
      );
    }

    // Default: External links open in new tab
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }, [navigate, token.colorPrimary]);

  // Navigation card: blockquote answers become a compass-labelled card
  const blockquoteRenderer = useCallback(({ children }) => (
    <blockquote className="vinny-nav-card">
      <CompassOutlined className="vinny-nav-card__icon" />
      <div className="vinny-nav-card__content">{children}</div>
    </blockquote>
  ), []);

  // Section headers ("### Steps") get a consistent list icon
  const headingRenderer = useCallback(({ children }) => (
    <h3>
      <OrderedListOutlined className="vinny-h3-icon" />
      {children}
    </h3>
  ), []);

  // Result/Tip lines become iconed callouts instead of emoji-prefixed text
  const paragraphRenderer = useCallback(({ children }) => {
    const text = getNodeText(children).trim();
    if (/^Result\s*:/i.test(text)) {
      return (
        <p className="vinny-callout vinny-callout--result">
          <CheckCircleFilled className="vinny-callout__icon" />
          <span className="vinny-callout__text">{children}</span>
        </p>
      );
    }
    if (/^Tip\s*:/i.test(text)) {
      return (
        <p className="vinny-callout vinny-callout--tip">
          <BulbFilled className="vinny-callout__icon" />
          <span className="vinny-callout__text">{children}</span>
        </p>
      );
    }
    return <p>{children}</p>;
  }, []);

  const markdownComponents = useMemo(() => ({
    a: linkRenderer,
    blockquote: blockquoteRenderer,
    h3: headingRenderer,
    p: paragraphRenderer,
  }), [linkRenderer, blockquoteRenderer, headingRenderer, paragraphRenderer]);

  const initialMessage = useMemo(
    () => ({
      id: 'welcome',
      sender: 'bot',
      text: welcomeMessage,
      isError: false,
    }),
    [welcomeMessage],
  );

  // Build user-specific localStorage keys
  const threadIdKey = userId ? `${VINNY_THREAD_ID_PREFIX}${userId}` : null;
  const messagesKey = userId ? `${VINNY_MESSAGES_PREFIX}${userId}` : null;

  // Initialize threadId from localStorage (user-specific)
  const [threadId, setThreadId] = useState(() => {
    if (!threadIdKey) return null;
    try {
      return localStorage.getItem(threadIdKey) || null;
    } catch (e) {
      console.warn('[VinnyChat] Could not read threadId from localStorage:', e);
      return null;
    }
  });

  // Initialize messages from localStorage (user-specific), falling back to initial welcome message
  const [messages, setMessages] = useState(() => {
    if (!messagesKey) return [initialMessage];
    try {
      const stored = localStorage.getItem(messagesKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('[VinnyChat] Could not read messages from localStorage:', e);
    }
    return [initialMessage];
  });

  // Reset chat state when userId changes (user switched accounts)
  useEffect(() => {
    if (!userId) return;
    // Load data for the new user
    try {
      const storedThreadId = localStorage.getItem(threadIdKey) || null;
      setThreadId(storedThreadId);

      const storedMessages = localStorage.getItem(messagesKey);
      if (storedMessages) {
        const parsed = JSON.parse(storedMessages);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          return;
        }
      }
      setMessages([initialMessage]);
    } catch (e) {
      console.warn('[VinnyChat] Could not load user-specific chat data:', e);
      setMessages([initialMessage]);
      setThreadId(null);
    }
  }, [userId, threadIdKey, messagesKey, initialMessage]);

  // Persist threadId to localStorage whenever it changes (user-specific)
  useEffect(() => {
    if (!threadIdKey) return;
    try {
      if (threadId) {
        localStorage.setItem(threadIdKey, threadId);
      }
    } catch (e) {
      console.warn('[VinnyChat] Could not save threadId to localStorage:', e);
    }
  }, [threadId, threadIdKey]);

  // Persist messages to localStorage whenever they change (user-specific)
  useEffect(() => {
    if (!messagesKey) return;
    try {
      // Only persist if we have more than just the welcome message
      if (messages.length > 0) {
        localStorage.setItem(messagesKey, JSON.stringify(messages));
      }
    } catch (e) {
      console.warn('[VinnyChat] Could not save messages to localStorage:', e);
    }
  }, [messages, messagesKey]);

  useEffect(() => { }, [apiEndpoint]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const node = messageContainerRef.current;
    if (!node) {
      return;
    }
    const rafId = window.requestAnimationFrame(() => {
      node.scrollTo({
        top: node.scrollHeight,
        behavior: 'smooth',
      });
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [messages, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    textAreaRef.current?.focus({ preventScroll: true });
  }, [isOpen]);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const appendMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const handleClear = useCallback(() => {
    setMessages([initialMessage]);
    setThreadId(null);
    // Clear user-specific localStorage to ensure fresh start
    try {
      if (threadIdKey) localStorage.removeItem(threadIdKey);
      if (messagesKey) localStorage.removeItem(messagesKey);
    } catch (e) {
      console.warn('[VinnyChat] Could not clear localStorage:', e);
    }
  }, [initialMessage, threadIdKey, messagesKey]);

  // Robust copy function with fallback
  const handleCopy = useCallback((text) => {
    if (navigator.clipboard && window.isSecureContext) {
      // Modern way (HTTPS or localhost)
      navigator.clipboard.writeText(text).catch((err) => {
        console.error('Modern copy failed: ', err);
      });
    } else {
      // Fallback way (for HTTP sites)
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.top = '-9999px';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (err) {
        console.error('Fallback copy failed: ', err);
      }
    }
  }, []);

  const handleSend = useCallback(async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || isLoading) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: trimmedValue,
      isError: false,
    };

    appendMessage(userMessage);
    setInputValue('');
    setIsLoading(true);
    textAreaRef.current?.focus({ preventScroll: true });

    // L2 Support Trigger Logic
    const lowerCaseInput = trimmedValue.toLowerCase();
    const triggerPhrases = [
      'raise a ticket',
      'raise ticket',
      'not happy',
      'human support',
      'support ticket',
      'contact support',
      'talk to a human'
    ];

    const isSupportTriggered = triggerPhrases.some(phrase => lowerCaseInput.includes(phrase));

    if (isSupportTriggered) {
      setTimeout(() => {
        appendMessage({
          id: `ticket-form-${Date.now()}`,
          sender: 'bot',
          type: 'ticket-form',
          text: 'I understand you want to escalate this. Please fill out the form below to raise a support ticket.',
          isError: false,
        });
        setIsLoading(false);
      }, 600); // Small delay for natural feel
      return;
    }

    try {
      const { data: payload } = await axios.post(apiEndpoint, {
        question: trimmedValue,
        threadId: threadId || undefined,
        currentScreen: window.location.pathname,
      });

      setThreadId((prevThreadId) => payload.threadId ?? prevThreadId);
      const resolvedText =
        resolveResponseText(payload) ||
        NO_ANSWER_MESSAGE;

      appendMessage({
        id: `bot-${Date.now()}`,
        sender: 'bot',
        text: resolvedText,
        isError: false,
        messageId: payload.messageId || null,
        feedbackRating: null,
        userQuestion: trimmedValue,
        imageUrl: payload.imageUrl || null,
        showTicketButton: FAILURE_PHRASES.some(phrase => resolvedText.includes(phrase))
      });
    } catch (error) {
      appendMessage({
        id: `error-${Date.now()}`,
        sender: 'bot',
        text: DEFAULT_ERROR_MESSAGE,
        isError: true,
        showTicketButton: true // Also allow raising ticket on connection error? Maybe good idea.
      });
    } finally {
      setIsLoading(false);
      textAreaRef.current?.focus({ preventScroll: true });
    }
  }, [appendMessage, inputValue, isLoading, apiEndpoint, threadId]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Submit feedback for a message
  const submitFeedback = useCallback(async (msgId, rating, userQuestion, answer) => {
    try {
      await axios.post('/vinny/chat/feedback', {
        rating,
        messageId: msgId,
        threadId,
        userQuestion,
        answer,
      });
      // Update local state to show selected rating
      setMessages(prev => prev.map(msg =>
        msg.messageId === msgId ? { ...msg, feedbackRating: rating } : msg
      ));
    } catch (error) {
      console.error('[VinnyChat] Failed to submit feedback:', error);
    }
  }, [threadId]);

  const iconLabel = useMemo(
    () => (isOpen ? 'Close chat' : 'Open chat'),
    [isOpen],
  );

  const welcomeTooltip = "Hi! I'm Vinny. Ask me anything!";

  return (
    <div
      className="vinny-chat-container"
      aria-live="polite"
    // Note: The zIndex style prop has been removed from here
    >
      {!isOpen && (
        <Tooltip title={welcomeTooltip} placement="left">
          <button
            type="button"
            className="vinny-chat-toggle"
            aria-label={iconLabel}
            onClick={toggleOpen}
            style={{ backgroundColor: token.colorPrimary }}
          >
            <VinnyIcon style={{ color: '#fff', fontSize: '28px' }} />
          </button>
        </Tooltip>
      )}

      {isOpen && (
        <div
          className="vinny-chat-window vinny-chat-window--open"
          role="dialog"
          aria-modal="false"
          aria-label={`${title} chat window`}
        >
          <header
            className="vinny-chat-header"
            style={{ backgroundColor: token.colorPrimary }}
          >
            <div className="vinny-chat-header__icon">
              <VinnyIcon style={{ color: '#fff', fontSize: '24px' }} />
            </div>
            <div className="vinny-chat-header__title-section">
              <div className="vinny-chat-header__title">{title}</div>
              {/* Subtitle div is removed */}
            </div>
            <div className="vinny-chat-header__actions">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined style={{ color: '#fff' }} />}
                aria-label="Refresh conversation"
                onClick={handleClear} // This button works
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined style={{ color: '#fff' }} />}
                aria-label="Close chat"
                onClick={closeChat}
              />
            </div>
          </header>

          <div className="vinny-chat-body" ref={messageContainerRef}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`vinny-chat-message vinny-chat-message--${message.sender
                  }${message.isError ? ' vinny-chat-message--error' : ''}`}
              >
                {/* Bot Avatar */}
                {message.sender === 'bot' && (
                  <div className="vinny-chat-message__avatar">
                    <VinnyIcon />
                  </div>
                )}

                <div className="vinny-chat-message__content-wrapper">
                  <div className="vinny-chat-bubble">
                    <ReactMarkdown components={markdownComponents}>
                      {message.sender === 'bot' ? stripLegacyEmojiMarkers(message.text) : message.text}
                    </ReactMarkdown>

                    {message.imageUrl && (
                      <img
                        src={message.imageUrl}
                        alt="Context"
                        className="vinny-chat-bubble__image"
                      />
                    )}
                  </div>

                  {/* Support Ticket Form Rendering */}
                  {message.type === 'ticket-form' && (
                    <SupportTicketForm
                      initialContext={messages.slice(-5).map(m => `${m.sender}: ${m.text}`).join('\n')}
                      initialEmail={userEmail}
                      onSubmitSuccess={() => {
                        appendMessage({
                          id: `bot-ack-${Date.now()}`,
                          sender: 'bot',
                          text: 'Thank you. Your ticket has been logged and our team has been notified. We will get back to you as soon as possible.',
                          isError: false,
                        });
                      }}
                    />
                  )}

                  {message.sender === 'bot' &&
                    !message.isError &&
                    message.id !== 'welcome' && (
                      <div className="vinny-chat-message__actions">
                        {message.messageId && (
                          <>
                            <Tooltip title="Helpful">
                              <Button
                                type="text"
                                size="small"
                                icon={message.feedbackRating === 'positive' ? <LikeFilled /> : <LikeOutlined />}
                                onClick={() => submitFeedback(message.messageId, 'positive', message.userQuestion, message.text)}
                                style={{
                                  color: message.feedbackRating === 'positive' ? '#52c41a' : undefined,
                                  fontSize: '17px',
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Not helpful">
                              <Button
                                type="text"
                                size="small"
                                icon={message.feedbackRating === 'negative' ? <DislikeFilled /> : <DislikeOutlined />}
                                onClick={() => submitFeedback(message.messageId, 'negative', message.userQuestion, message.text)}
                                style={{
                                  color: message.feedbackRating === 'negative' ? '#ff4d4f' : undefined,
                                  fontSize: '17px',
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Copy text">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopy(message.text)}
                          />
                        </Tooltip>
                      </div>
                    )}

                  {/* Fallback Ticket Button */}
                  {message.showTicketButton && (
                    <div style={{ marginTop: 8, marginLeft: 4 }}>
                      <Button
                        size="small"
                        type="default"
                        style={{ borderRadius: '12px', fontSize: '12px' }}
                        onClick={() => {
                          appendMessage({
                            id: `ticket-form-${Date.now()}`,
                            sender: 'bot',
                            type: 'ticket-form',
                            text: 'Please fill out the form below.',
                            isError: false,
                          });
                        }}
                      >
                        Raise Support Ticket
                      </Button>
                    </div>
                  )}
                </div>

                {/* --- THIS IS THE NEW USER AVATAR --- */}
                {message.sender === 'user' && (
                  <div className="vinny-chat-message__avatar vinny-chat-message__avatar--user">
                    <UserOutlined />
                  </div>
                )}
                {/* --- END NEW USER AVATAR --- */}

              </div>
            ))}

            {isLoading && (
              <div className="vinny-chat-message vinny-chat-message--bot">
                <div className="vinny-chat-message__avatar">
                  <VinnyIcon />
                </div>
                <div className="vinny-chat-bubble vinny-chat-bubble--typing">
                  <span className="vinny-chat-typing">
                    <span className="vinny-chat-typing__dot" />
                    <span className="vinny-chat-typing__dot" />
                    <span className="vinny-chat-typing__dot" />
                  </span>
                </div>
              </div>
            )}
          </div>

          <footer className="vinny-chat-footer">
            <TextArea
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="vinny-chat-input"
              aria-label="Message Vinny"
              ref={textAreaRef}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              className="vinny-chat-send"
              aria-label="Send message"
            />
          </footer>
        </div>
      )}
    </div>
  );
};

export default VinnyChat;